import { Button, Modal, TagsInput } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import React, { useState } from 'react';
import styles from './b2b.module.css';
import { formatDay, money, units } from './utils';

/**
 * Sending the PI, as on a production order: who it goes to is shown and editable before it
 * leaves (pre-filled with the customer and the brand's PI recipients), or it is issued without
 * an email for a brand that shares the PDF itself. The confirm for that replaces the form, so
 * the dialog is never about two things at once.
 */
const SendPiModal: React.FC<{
    orderId: string;
    customerName: string;
    revision: number;
    validTill: string;
    summary: { skus: number; units: number; payable: number };
    defaults: { to: string[]; cc: string[] };
    savesChanges: boolean;
    sending: boolean;
    error: string | null;
    onClose: () => void;
    onSend: (send: { to?: string[]; cc?: string[]; withoutEmail?: boolean }) => void;
}> = ({ orderId, customerName, revision, validTill, summary, defaults, savesChanges, sending, error, onClose, onSend }) => {
    const [to, setTo] = useState<string[]>(defaults.to);
    const [cc, setCc] = useState<string[]>(defaults.cc);
    const [confirmingNoEmail, setConfirmingNoEmail] = useState(false);
    const what = revision > 1 ? `revised PI (Rev ${revision})` : 'PI';

    return (
        <Modal opened onClose={onClose} title={confirmingNoEmail ? `Issue the ${what} without emailing?` : `Send ${what} for ${orderId}`} centered size="lg">
            {confirmingNoEmail ? (
                <div className={styles.stack}>
                    <span className={styles.fieldValue}>
                        The PI is stored and the order moves to PI Issued, but no email goes out. {customerName} still sees it on their portal; download the PDF to share
                        it yourself.
                    </span>
                    <div className={styles.rowEnd}>
                        <Button variant={ButtonVariant.DEFAULT} disabled={sending} onClick={() => setConfirmingNoEmail(false)}>
                            Go back
                        </Button>
                        <Button variant={ButtonVariant.ACTION} loading={sending} onClick={() => onSend({ withoutEmail: true })}>
                            Issue without emailing
                        </Button>
                    </div>
                </div>
            ) : (
                <div className={styles.stack}>
                    <span className={styles.fieldValue}>
                        {customerName} will receive the proforma invoice as a PDF: {summary.skus} {summary.skus === 1 ? 'SKU' : 'SKUs'}, {units(summary.units)} units, payable{' '}
                        {money(summary.payable)}, valid till {formatDay(validTill)}.
                    </span>
                    <TagsInput label="To" description="Type an address and press Enter." placeholder="name@company.com" value={to} onChange={setTo} />
                    <TagsInput label="CC" description="Anyone else who should get this PI." placeholder="name@company.com" value={cc} onChange={setCc} />
                    <span className={styles.muted}>It also appears on their portal, where they confirm it and upload their payment.</span>
                    {savesChanges ? <span className={styles.muted}>Your changes to the lines are saved first.</span> : null}
                    {error ? <span className={styles.error}>{error}</span> : null}
                    <div className={styles.between}>
                        <Button variant={ButtonVariant.DEFAULT} disabled={sending} onClick={() => setConfirmingNoEmail(true)}>
                            Issue without emailing
                        </Button>
                        <div className={styles.row}>
                            <Button variant={ButtonVariant.DEFAULT} disabled={sending} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button variant={ButtonVariant.ACTION} loading={sending} disabled={to.length === 0} onClick={() => onSend({ to, cc })}>
                                Send {what}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default SendPiModal;
